import { Environment } from '@/constants/common';

/*
 * Kentico Xperience hosts the production website on its own stage domain
 * as `prod-bobw-<dynamic hash>.xperience-sites.com`, so match by pattern.
 */
const kenticoProductionHostnamePattern = /^prod-bobw-[a-z0-9]+\.xperience-sites\.com$/iu;
// Deployment hashes and branch aliases belong to this Pages project only.
const bookingPreviewHostnamePattern = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.booking-webapp-stage\.pages\.dev$/iu;

export function isBookingPreviewHostname(hostname: string): boolean {
  return bookingPreviewHostnamePattern.test(hostname);
}

export function isKenticoProductionHostname(hostname: string) : boolean {
  return kenticoProductionHostnamePattern.test(hostname);
}

export function transformEnvironment(environment: string | null) : Environment {
  switch (environment) {
    case 'prod': {
      return Environment.Production;
    }

    case 'stage': {
      return Environment.Stage;
    }

    case 'dukenukem': {
      return Environment.Dukenukem;
    }

    default: {
      return Environment.Development;
    }
  }
}

export function forceEnvironment(newEnvironment: string) : void {
  localStorage.setItem('environment', newEnvironment);

  location.reload();
}

export function resetEnvironment() : void {
  localStorage.removeItem('environment');

  location.reload();
}

export function getForcedEnvironment() : Environment | undefined {
  const possibleEnvironment = localStorage.getItem('environment');

  if (possibleEnvironment === null) {
    return undefined;
  }

  return transformEnvironment(possibleEnvironment);
}

export function getRealEnvironment() : Environment {
  const { hostname } = window.location;

  if (isKenticoProductionHostname(hostname)) {
    return Environment.Production;
  }

  switch (hostname) {
    case 'booking.bobw.co':
    case 'bobw.co': {
      return Environment.Production;
    }

    case 'booking.staging-bobw.com':
    case 'preview.bobw.co': {
      return Environment.Stage;
    }

    case 'booking.dukenukem.test-bobw.com': {
      return Environment.Dukenukem;
    }

    default: {
      return Environment.Development;
    }
  }
}

/**
 * Environment whose analytics destinations (Segment, GTM) receive events.
 * The Kentico stage host runs as production but must not pollute production analytics.
 */
export function getAnalyticsEnvironment() : Environment {
  if (isKenticoProductionHostname(window.location.hostname)) {
    return Environment.Stage;
  }

  return getRealEnvironment();
}

export function getIsProductionEnvironment() : boolean {
  return getRealEnvironment() === Environment.Production;
}

export function getIsStageEnvironment() : boolean {
  return getRealEnvironment() === Environment.Stage;
}

export function getIsDevelopmentEnvironment() : boolean {
  return getRealEnvironment() === Environment.Development;
}

export function getEnvironment() : Environment {
  const realEnvironment = getRealEnvironment();

  // Always use production environment in production
  if (realEnvironment === Environment.Production) {
    return realEnvironment;
  }

  return getForcedEnvironment() ?? realEnvironment;
}
